import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { IoMdClose } from "react-icons/io";
import { LuChevronsUpDown } from "react-icons/lu";
import { FaCheck } from "react-icons/fa";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { CURRENT_DEFAULT_SYMBOL } from "@/config/logos";
import Fuse from "fuse.js";

type SymbolItem = {
  symbol: string;
  name: string;
  short_name?: string;
};

interface SymbolSelectorProps {
  value: string;
  onChange: (symbol: string) => void;
  symbolData: SymbolItem[];
  trendingSymbols: SymbolItem[];
  loading: boolean;
  t: (key: string) => string;
  clearSymbol: () => void;
  placeholder: string;
}

export const SymbolSelector = ({
  value,
  onChange,
  symbolData,
  trendingSymbols,
  loading,
  t,
  clearSymbol,
  placeholder,
}: SymbolSelectorProps) => {
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [instrumentInput, setInstrumentInput] = useState("");
  const [filteredSymbols, setFilteredSymbols] = useState<SymbolItem[]>([]);

  const displayValue =
    value &&
    (symbolData.concat(trendingSymbols).find((item) => item.symbol === value)
      ?.symbol ||
      "");

  const fuse = useMemo(() => {
    if (!symbolData.length) return null;
    return new Fuse(symbolData, {
      keys: ["name", "symbol", "short_name"],
      threshold: 0.4,
    });
  }, [symbolData]);

  useEffect(() => {
    if (!fuse) return;
    if (instrumentInput.trim()) {
      const results = fuse.search(instrumentInput);
      setFilteredSymbols(results.map((res) => res.item));
    } else {
      setFilteredSymbols(symbolData);
    }
  }, [instrumentInput, fuse, symbolData]);

  const handleSelect = (symbol: string) => {
    onChange(symbol);
    setShowSuggestion(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    clearSymbol();
    setShowSuggestion(false);
  };

  const renderSymbolItem = (item: SymbolItem, index: number) => {
    const truncatedName =
      item.name.length > 50 ? item.name.slice(0, 50) + "..." : item.name;

    return (
      <div
        key={`${item.symbol}_${index}`}
        onClick={() => handleSelect(item.symbol)}
        className="flex flex-row gap-2 w-full items-center hover:bg-[hsl(var(--background))] rounded-sm cursor-pointer"
      >
        <FaCheck
          className={cn(
            "mr-2 h-4 w-4",
            item.symbol === value ? "opacity-100" : "opacity-0"
          )}
        />
        <div className="flex flex-col text-sm w-full py-2">
          <div>{item.symbol}</div>
          <Separator />
          <div
            title={item.name}
            className="text-muted-foreground text-xs truncate pr-4"
          >
            {truncatedName}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-2 w-full relative">
      <Popover open={showSuggestion} onOpenChange={setShowSuggestion}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className={cn(
              "justify-between  text-[--marketplace-blue-new] border-[--marketplace-blue-new]"
            )}
          >
            <div className="flex grow">{displayValue || t(placeholder)}</div>
            <div className="flex flex-row items-center">
              {value && (
                <>
                  <Button size="icon" variant="ghost" onClick={handleClear}>
                    <IoMdClose />
                  </Button>
                  <Separator orientation="vertical" />
                </>
              )}
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0 ">
          <div className="w-full ">
            <input
              type="text"
              value={instrumentInput}
              onChange={(e) => setInstrumentInput(e.target.value)}
              className="w-full p-3 border text-xs bg-transparent outline-none"
              placeholder={`Example: Start typing ${CURRENT_DEFAULT_SYMBOL}...`}
            />
            <div className="w-screen lg:max-w-sm max-h-72 overflow-y-auto border rounded-md pr-4">
              {loading && instrumentInput ? (
                <div className="flex justify-center p-4">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : instrumentInput ? (
                filteredSymbols.length > 0 ? (
                  filteredSymbols.map(renderSymbolItem)
                ) : (
                  <div className="w-full flex justify-center p-4">
                    {t("No symbol found")}
                  </div>
                )
              ) : trendingSymbols.length > 0 ? (
                <>
                  <div className="text-center p-4 text-sm">
                    {t("Trending assets")}
                  </div>
                  <div className="border-t my-1" />
                  {trendingSymbols.map(renderSymbolItem)}
                </>
              ) : null}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
